import React, {ReactNode, useContext} from 'react';
import { Trans, useTranslation } from 'react-i18next'
import { BasicButton } from '@pokemon/design.ui.input.basic-button'
import { Form, Field } from 'react-final-form'
import { TextField } from '@pokemon/design.ui.input.text-field'
import { PasswordField, validatePassword } from '@pokemon/design.ui.input.password-field'
import { LoadingContext } from '@pokemon/design.ui.loading-spinner'
import './create-username-password-page.css'

export const validateUsername = function (t, screenname) {
    return (username: string) => {
        if (!username || username.trim().length === 0) {
            return t('username.required', 'Username is a required field.')
        } else if (!username.match(/^[0-9a-zA-ZÀ-ÿ_+]+$/)) {
            return t('username.characters', 'Username must contain only valid characters.')
        } else if (username.length < 6) {
            return t('username.min_length', 'Username must be more than 6 characters long.')
        } else if (username.length > 16) {
            return t('username.max_length', 'Username must be less than 17 characters long.')
        } else if (username === screenname) {
            return t('error_username_is_screenname', 'Your username cannot be the same as your screenname.')
        } else {
            return null
        }
    }
}

export type CreateUsernamePasswordPageProps = {
  /**
   * a node to be rendered in the special component.
   */
  children?: ReactNode;
  /**
   * a boolean to determine if account is for adult or child
   */
  isChild: boolean;
  /**
   * an async function to validate username
   *
   *    returning values:
   *        "cleared" = username is good to use
   *        "in_use" = username is already taken
   *        undefined = internal server error
   *
   */
  checkUsername: (username: string) => Promise<string | undefined>;
  /**
   * a function called once the form has been submitted and cleared
   */
  onClearedLoginInfo: (values: any) => void;
  /**
   * user's screenname that cannot match username
   */
  screenname?: string;
  /**
   * a function for handling errors
   */
  setError: (error: string) => void;
};

export function CreateUsernamePasswordPage({ isChild, checkUsername, onClearedLoginInfo, screenname, setError, children }: CreateUsernamePasswordPageProps) {
  const { setLoading } = useContext(LoadingContext)
  const { t} = useTranslation()
  const onSubmit = (t, setLoading) => async (values, _, callback) => {
    setLoading(true)
    const isUsernameTaken = await checkUsername(values.username)
    if (isUsernameTaken === 'cleared') {
        onClearedLoginInfo(values)
    } else if (isUsernameTaken === 'in_use') {
        callback({ username: t('username.server_invalid', 'Your username is already taken.') })
        setLoading(false)
        return
    } else {
        setError(t('error_could_not_check_username', 'An unexpected error occurred, please try again later.'))
    }
    callback()
    setLoading(false)
  }

  return <Form onSubmit={onSubmit(t, setLoading)}
    render={
      ({ handleSubmit, submitting, hasSubmitErrors, hasValidationErrors, modifiedSinceLastSubmit, form }) => (
      <form onSubmit={handleSubmit}>
        <div className="login-info">
          <h1>{isChild ? t('child_login_info', 'Child\'s Login Information') : t('registration_header', 'Enter the username and password')}</h1>
          <p>{isChild ? t('enter_login_info', 'Enter the username and password you would like to use to log in to your account') : null}</p>
        </div>
        <Field name="username"
          validate={validateUsername(t, screenname)} >
        {({input, meta}) => (
          <React.Fragment>
            <TextField name="Username"
              placeholder={t('username_placeholder', 'Username')}
              errorText={(meta.error || meta.submitError) && meta.touched && meta.error || meta.submitError}
              {...input} />
          </React.Fragment>
        )}
        </Field>
        <div style={{marginTop: '8px', marginBottom: '16px', textAlign: 'left'}}>
          <Trans
            t={t}
            i18nKey='do_not_share_username'
            defaults='You will use your username to log in to your account, or connected apps. &lt;strong&gt;Do not share your username&lt;/strong&gt; and it &lt;strong&gt;needs to be different than your screen name.&lt;/strong&gt;'
            components={{strong: <strong/>}} />
        </div>
        <Field name="password"
          validate={text => !validatePassword(text)} >
          {({input, meta}) => (
            <React.Fragment>
              <PasswordField input={input}/>
            </React.Fragment>
          )}
        </Field>
        <BasicButton
          text={isChild ? t('child_create_account_button','Complete Add a Child') : t('create_account_button', 'Create Account')}
          type="submit"
          data-testid="create-account-button"
          disabled={submitting || hasValidationErrors || (hasSubmitErrors && !modifiedSinceLastSubmit)} />
          {children}
      </form>
    )}>
  </Form>
}
